import React, { useLayoutEffect, useRef } from 'react';

import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { addUFOCustomCohortData } from '@atlaskit/react-ufo/add-ufo-custom-cohort-data';
import { addUFOCustomData } from '@atlaskit/react-ufo/add-ufo-custom-data';
import UFOCustomData from '@atlaskit/react-ufo/custom-data';
import { useSmartCreationModalSurface } from '@atlassian/conversation-assistant-store/controllers/chat-context/store';
import { useStagingArea } from '@atlassian/conversation-assistant-store/controllers/staging-area';

import { useWindowLocation } from '../../common/utils/use-window-location/useWindowLocation';
import type { ConversationAssistantUIModes } from '../../types';

/**
 * This component sets few attributes that will be read by UFO (Unified Frontend Observability) team
 * This will help to determine the existence of Rovo chat sidebar on the page
 * because Rovo chat sidebar is going to impact performance of the page
 *
 * Context: https://atlassian.slack.com/archives/C069T3BQ6DB/p1747703260865699?thread_ts=1747280779.858379&cid=C069T3BQ6DB
 */
export const RovoChatUFOStatePublisher = ({
	fullscreen,
	mode,
	shouldPublishChatOpenOnTransitionData = true,
}: {
	fullscreen: boolean | undefined;
	mode: ConversationAssistantUIModes;
	shouldPublishChatOpenOnTransitionData?: boolean;
}): React.JSX.Element => {
	const location = useWindowLocation();
	const previousLocationRef = useRef(location);
	const [isChatMountedAcrossTransition, setIsChatMountedAcrossTransition] = React.useState(false);
	let conversationAssistantMode = 'unknown';
	const [{ stagingAreaMode }] = useStagingArea();
	const [smartCreationModalSurface] = useSmartCreationModalSurface();
	const isSmartCreationSurfaceTrackingEnabled = isExperimentEnabled(
		'cwr_entrypoint_parity_staging_area_mode_enum',
	);
	if (isSmartCreationSurfaceTrackingEnabled && smartCreationModalSurface === 'prompt') {
		conversationAssistantMode = 'aifc-prompt';
	} else if (
		(isSmartCreationSurfaceTrackingEnabled && smartCreationModalSurface === 'staging') ||
		stagingAreaMode === 'modal' ||
		stagingAreaMode === 'ephemeralModal'
	) {
		conversationAssistantMode = 'aifc-modal';
	} else if (fullscreen) {
		conversationAssistantMode = 'fullscreen';
	} else if (mode === 'mini-modal') {
		conversationAssistantMode = 'mini-modal';
	} else if (mode === 'sidebar') {
		conversationAssistantMode = 'sidebar';
	}

	useLayoutEffect(() => {
		if (!fullscreen) {
			fg('platform_ufo_custom_cohort_data_migration')
				? addUFOCustomCohortData('isRovoSidebarVisible', true)
				: addUFOCustomData({ isRovoSidebarVisible: true });
		}

		if (previousLocationRef.current !== location) {
			previousLocationRef.current = location;
			setIsChatMountedAcrossTransition(true);
		}

		return () => {
			fg('platform_ufo_custom_cohort_data_migration')
				? addUFOCustomCohortData('isRovoSidebarVisible', false)
				: addUFOCustomData({ isRovoSidebarVisible: false });
		};
	}, [fullscreen, location]);

	return (
		<UFOCustomData
			data={{
				conversationAssistantMode: conversationAssistantMode,
				...(shouldPublishChatOpenOnTransitionData ? { isChatMountedAcrossTransition } : undefined),
			}}
		/>
	);
};
